/**
 * KpiCard — token-driven KPI tile combining a headline value, a delta
 * indicator, and an optional sparkline trajectory.
 *
 * Stands in for the @tremor/react `KpiCard` referenced in issue #1581
 * without introducing a React-18-locked peer dependency. The component
 * composes the existing {@link Card} primitive and {@link MetricSparkline}
 * so the KPI surface stays visually consistent with the rest of the
 * SecuritySurfacePanel family.
 *
 * Tone vocabulary matches the rest of the library: `safe` for
 * structural-immunity wins, `warn` for vulnerable / regressing series,
 * `neutral` for diagnostic surfaces. The tone selects a token-aligned
 * accent stripe and delta-arrow color without forcing a hard-coded hex.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";
import { Card } from "../Card.js";
import { MetricSparkline, type MetricSparklineProps } from "./MetricSparkline.js";

export type KpiCardTone = "neutral" | "safe" | "warn" | "info";

export interface KpiCardDelta {
  /** Numeric delta. Sign decides the arrow direction. */
  readonly value: number;
  /**
   * Whether an upward movement is the desired direction. Defaults to
   * `true`. When `false`, a positive delta renders as a warn-tone
   * regression (e.g. attack-surface size that should shrink).
   */
  readonly upIsGood?: boolean;
  /** Suffix appended after the formatted delta (e.g. `%`, `bps`). */
  readonly suffix?: string;
  /** Optional caption shown alongside the delta. */
  readonly caption?: string;
}

export interface KpiCardProps {
  /** Short label above the headline value. */
  readonly label: string;
  /** Headline value — accepts a pre-formatted string or a number. */
  readonly value: number | string;
  /** Optional suffix rendered after the headline value (`%`, `μ`, etc.). */
  readonly valueSuffix?: string;
  /** Optional delta indicator. */
  readonly delta?: KpiCardDelta;
  /** Optional sparkline. */
  readonly sparkline?: Omit<MetricSparklineProps, "ariaLabel"> & {
    readonly ariaLabel?: string;
  };
  /** Visual tone. Defaults to `neutral`. */
  readonly tone?: KpiCardTone;
  /** Caption rendered below the headline value. */
  readonly caption?: React.ReactNode;
  /** Accessible label override. Defaults to `${label}: ${value}`. */
  readonly ariaLabel?: string;
}

const TONE_ACCENT: Readonly<Record<KpiCardTone, string>> = {
  neutral: GRAY[400],
  safe: SAFE[500],
  warn: WARN[500],
  info: PRIMARY[500],
} as const;

const TONE_VALUE_COLOR: Readonly<Record<KpiCardTone, string>> = {
  neutral: GRAY[900],
  safe: SAFE[700],
  warn: WARN[700],
  info: PRIMARY[700],
} as const;

function formatDelta(delta: number): string {
  if (delta === 0) return "0";
  const sign = delta > 0 ? "+" : "−";
  return `${sign}${Math.abs(delta)}`;
}

function deltaTone(delta: KpiCardDelta): KpiCardTone {
  if (delta.value === 0) return "neutral";
  const upIsGood = delta.upIsGood ?? true;
  const positive = delta.value > 0;
  return positive === upIsGood ? "safe" : "warn";
}

function deltaArrow(delta: KpiCardDelta): string {
  if (delta.value === 0) return "→";
  return delta.value > 0 ? "↑" : "↓";
}

export const KpiCard = React.memo(function KpiCard({
  label,
  value,
  valueSuffix,
  delta,
  sparkline,
  tone = "neutral",
  caption,
  ariaLabel,
}: KpiCardProps): React.JSX.Element {
  const accent = TONE_ACCENT[tone];
  const valueColor = TONE_VALUE_COLOR[tone];
  const resolvedAriaLabel = ariaLabel ?? `${label}: ${String(value)}${valueSuffix ?? ""}`;
  const computedDeltaTone = delta !== undefined ? deltaTone(delta) : "neutral";

  return (
    <Card accent={accent} aria-label={resolvedAriaLabel} style={{ minWidth: 180 }}>
      <div
        data-csk-kpi-label
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: GRAY[600],
        }}
      >
        {label}
      </div>
      <div
        data-csk-kpi-value
        style={{
          marginTop: 6,
          color: valueColor,
          fontSize: 24,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
        {valueSuffix !== undefined && (
          <span style={{ marginLeft: 4, fontSize: 14, color: GRAY[600], fontWeight: 500 }}>
            {valueSuffix}
          </span>
        )}
      </div>
      {delta !== undefined && (
        <div
          data-csk-kpi-delta
          data-csk-delta-tone={computedDeltaTone}
          style={{
            marginTop: 4,
            fontSize: 12,
            color: TONE_VALUE_COLOR[computedDeltaTone],
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span aria-hidden="true">{deltaArrow(delta)}</span>
          <span>
            {formatDelta(delta.value)}
            {delta.suffix ?? ""}
          </span>
          {delta.caption !== undefined && <span style={{ color: GRAY[600] }}>{delta.caption}</span>}
        </div>
      )}
      {caption !== undefined && (
        <div style={{ marginTop: 6, fontSize: 12, color: GRAY[600] }}>{caption}</div>
      )}
      {sparkline !== undefined && (
        <div style={{ marginTop: 10 }}>
          <MetricSparkline {...sparkline} ariaLabel={sparkline.ariaLabel ?? `${label} trend`} />
        </div>
      )}
    </Card>
  );
});
